import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import {
  Store,
  createActionGroup,
  createFeature,
  createReducer,
  createSelector,
  on,
  props,
} from '@ngrx/store';
import { catchError, exhaustMap, filter, map, of } from 'rxjs';
import { EduContentTOCLearningDomainInterface } from '../../+models';
import {
  EDU_CONTENT_TOC_LEARNING_DOMAIN_SERVICE_TOKEN,
  EduContentTocLearningDomainMap,
} from '../../edu-content-toc-learning-domain';

export type QueryStatus = 'fetched' | 'failed';

export interface EduContentTocLearningDomainState {
  links: EduContentTOCLearningDomainInterface[];
  // Per-query outcome. `'fetched'` after a `loaded`, `'failed'` after a
  // `loadFailed`. A failure does not overwrite an earlier `'fetched'` so
  // cached data stays usable across refresh failures.
  queryStatus: Record<string, QueryStatus>;
}

export const initialState: EduContentTocLearningDomainState = {
  links: [],
  queryStatus: {},
};

// Canonical query signature for a (tocId, includeChildren, includeAncestors)
// tuple. Used both as a dedup key on the effect and as the gate the route
// resolver waits on.
export function tocLearningDomainQuery(
  eduContentTOCId: number,
  includeChildren: boolean,
  includeAncestors: boolean
): string {
  const childrenSegment = includeChildren ? 'children' : 'no-children';
  const ancestorsSegment = includeAncestors ? 'ancestors' : 'no-ancestors';
  return `toc:${eduContentTOCId}:${childrenSegment}:${ancestorsSegment}`;
}

// Canonical query signature for a book-level fetch (first-depth tocs of the
// book). Lives in the same `queryStatus` map as the toc signatures; the `book:`
// prefix keeps the namespaces from colliding.
export function bookLearningDomainQuery(eduContentBookId: number): string {
  return `book:${eduContentBookId}`;
}

export const actions = createActionGroup({
  source: 'EduContentTocLearningDomains',
  events: {
    loadFor: props<{
      eduContentTOCId: number;
      includeChildren?: boolean;
      includeAncestors?: boolean;
    }>(),
    loadForBook: props<{ eduContentBookId: number }>(),
    loaded: props<{ query: string; links: EduContentTOCLearningDomainInterface[] }>(),
    loadFailed: props<{ query: string; error: unknown }>(),
  },
});

// Backend returns a `{ eduContentTOCId: number[] }` map; we store one row per
// (toc, learning-domain) link so selectors can group/filter cleanly. Tocs with
// an empty LD list yield no rows but the query is still marked fetched so we
// don't refetch.
function flattenMap(map: EduContentTocLearningDomainMap): EduContentTOCLearningDomainInterface[] {
  const rows: EduContentTOCLearningDomainInterface[] = [];
  for (const [tocIdKey, learningDomainIds] of Object.entries(map)) {
    const eduContentTOCId = Number(tocIdKey);
    for (const learningDomainId of learningDomainIds) {
      rows.push({
        id: `${eduContentTOCId}-${learningDomainId}`,
        eduContentTOCId,
        learningDomainId,
      });
    }
  }
  return rows;
}

function mergeLinks(
  existing: EduContentTOCLearningDomainInterface[],
  incoming: EduContentTOCLearningDomainInterface[]
): EduContentTOCLearningDomainInterface[] {
  if (incoming.length === 0) return existing;
  const seen = new Set(existing.map((row) => row.id));
  const result = [...existing];
  for (const link of incoming) {
    if (!seen.has(link.id)) result.push(link);
  }
  return result;
}

export const feature = createFeature({
  name: 'eduContentTocLearningDomains',
  reducer: createReducer(
    initialState,
    on(
      actions.loaded,
      (state, { query, links }): EduContentTocLearningDomainState => ({
        ...state,
        links: mergeLinks(state.links, links),
        queryStatus: { ...state.queryStatus, [query]: 'fetched' },
      })
    ),
    on(
      actions.loadFailed,
      (state, { query }): EduContentTocLearningDomainState => ({
        ...state,
        queryStatus: state.queryStatus[query] === 'fetched'
          ? state.queryStatus
          : { ...state.queryStatus, [query]: 'failed' },
      })
    )
  ),
  extraSelectors: ({ selectLinks, selectQueryStatus }) => ({
    selectLearningDomainIdsByTocId: createSelector(selectLinks, (links) => {
      const byTocId: Record<number, number[]> = {};
      for (const link of links) {
        const bucket = byTocId[link.eduContentTOCId];
        if (bucket) bucket.push(link.learningDomainId);
        else byTocId[link.eduContentTOCId] = [link.learningDomainId];
      }
      return byTocId;
    }),
    selectIsQueryFetched: (query: string) =>
      createSelector(selectQueryStatus, (status) => status[query] === 'fetched'),
    // True when the query resolved (fetched or failed). Used by the route
    // resolver so navigation proceeds even when the API call errored — a failed
    // mapping fetch must never block the page from rendering.
    selectIsQueryAttempted: (query: string) =>
      createSelector(selectQueryStatus, (status) => status[query] != null),
  }),
});

export const loadFor$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    service = inject(EDU_CONTENT_TOC_LEARNING_DOMAIN_SERVICE_TOKEN)
  ) =>
    actions$.pipe(
      ofType(actions.loadFor),
      concatLatestFrom(({ eduContentTOCId, includeChildren, includeAncestors }) =>
        store.select(
          feature.selectIsQueryFetched(
            tocLearningDomainQuery(eduContentTOCId, !!includeChildren, !!includeAncestors)
          )
        )
      ),
      filter(([, alreadyFetched]) => !alreadyFetched),
      exhaustMap(([{ eduContentTOCId, includeChildren, includeAncestors }]) => {
        const query = tocLearningDomainQuery(eduContentTOCId, !!includeChildren, !!includeAncestors);
        return service
          .getForToc({
            eduContentTOCId,
            includeChildren: !!includeChildren,
            includeAncestors: !!includeAncestors,
          })
          .pipe(
            map((response) => actions.loaded({ query, links: flattenMap(response) })),
            catchError((error) => of(actions.loadFailed({ query, error })))
          );
      })
    ),
  { functional: true }
);

export const loadForBook$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    service = inject(EDU_CONTENT_TOC_LEARNING_DOMAIN_SERVICE_TOKEN)
  ) =>
    actions$.pipe(
      ofType(actions.loadForBook),
      concatLatestFrom(({ eduContentBookId }) =>
        store.select(feature.selectIsQueryFetched(bookLearningDomainQuery(eduContentBookId)))
      ),
      filter(([, alreadyFetched]) => !alreadyFetched),
      exhaustMap(([{ eduContentBookId }]) => {
        const query = bookLearningDomainQuery(eduContentBookId);
        return service.getForBook({ eduContentBookId }).pipe(
          map((response) => actions.loaded({ query, links: flattenMap(response) })),
          catchError((error) => of(actions.loadFailed({ query, error })))
        );
      })
    ),
  { functional: true }
);

export const effects = {
  loadFor$,
  loadForBook$,
};
